import { Row, RowSelectionState, Table } from '@tanstack/react-table';
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useRef,
} from 'react';

type CheckboxCheckedState = boolean | 'indeterminate';

type GetRangeRowSelectionParams<TData> = {
  anchorRowId: string;
  isSelected: boolean;
  row: Row<TData>;
  rowSelection: RowSelectionState | undefined;
  table: Table<TData>;
};

type UseShiftRangeRowSelectionParams = {
  rowSelection: RowSelectionState | undefined;
  setRowSelection: ((rowSelection: RowSelectionState) => void) | undefined;
};

const getRangeRowSelection = <TData,>({
  anchorRowId,
  isSelected,
  row,
  rowSelection,
  table,
}: GetRangeRowSelectionParams<TData>): RowSelectionState | undefined => {
  const rows = table.getRowModel().rows;
  const anchorRowIndex = rows.findIndex(
    (rangeRow) => rangeRow.id === anchorRowId,
  );
  const currentRowIndex = rows.findIndex(
    (rangeRow) => rangeRow.id === row.id,
  );

  if (anchorRowIndex < 0 || currentRowIndex < 0) {
    return undefined;
  }

  const endIndex = Math.max(anchorRowIndex, currentRowIndex);
  const nextRowSelection = { ...(rowSelection ?? {}) };
  const startIndex = Math.min(anchorRowIndex, currentRowIndex);

  for (let index = startIndex; index <= endIndex; index += 1) {
    const rangeRow = rows[index];

    if (!rangeRow?.getCanSelect()) {
      continue;
    }

    if (isSelected) {
      nextRowSelection[rangeRow.id] = true;
    } else {
      delete nextRowSelection[rangeRow.id];
    }
  }

  return nextRowSelection;
};

export const useShiftRangeRowSelection = <TData,>({
  rowSelection,
  setRowSelection,
}: UseShiftRangeRowSelectionParams) => {
  const isShiftRangeSelectionRef = useRef(false);
  const lastSelectedRowIdRef = useRef<string | null>(null);

  const handleSelectCheckboxClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      isShiftRangeSelectionRef.current = event.shiftKey;
    },
    [],
  );

  const handleSelectCheckboxKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        isShiftRangeSelectionRef.current = event.shiftKey;
      }
    },
    [],
  );

  const handleRowCheckedChange = useCallback(
    (
      row: Row<TData>,
      table: Table<TData>,
      value: CheckboxCheckedState,
    ) => {
      const anchorRowId = lastSelectedRowIdRef.current;
      const isSelected = value === true;
      const isShiftRangeSelection =
        isShiftRangeSelectionRef.current && !!anchorRowId;

      isShiftRangeSelectionRef.current = false;

      if (isShiftRangeSelection && anchorRowId && setRowSelection) {
        const nextRowSelection = getRangeRowSelection({
          anchorRowId,
          isSelected,
          row,
          rowSelection,
          table,
        });

        if (nextRowSelection) {
          setRowSelection(nextRowSelection);
          return;
        }
      }

      lastSelectedRowIdRef.current = row.id;
      row.toggleSelected(isSelected);
    },
    [rowSelection, setRowSelection],
  );

  return {
    handleRowCheckedChange,
    handleSelectCheckboxClick,
    handleSelectCheckboxKeyDown,
  };
};
